import dayjs from 'dayjs';
import isEqual from 'lodash/isEqual';
import { ReactNode, createContext, useCallback, useEffect, useRef, useState } from 'react';
import type { Stages } from '../../../api/process-client/process-client.type';
import type { TaskTypeValue } from '../../tasks-table/tasks-table.type';
import type {
  CachedProcessScope,
  EventScope,
  ProcessContextType,
  ProcessScope,
  ProcessWorkflow,
} from './process.context.type';

const ProcessContext = createContext({} as ProcessContextType);

/** The scope shape used when nothing usable is cached — every field of the type. */
const EMPTY_CACHED_SCOPE: CachedProcessScope = {
  portfolio: null,
  team: null,
  board: null,
  epic: null,
  workPeriod: null,
  task: null,
  workflow: null,
};

/**
 * Reads the cached process scope, falling back to an empty scope on any failure.
 *
 * Guarded and passed to `useState` lazily rather than evaluated inline, for two
 * reasons. A non-lazy initializer re-runs this read on *every* render of the
 * provider, and `ProcessProvider` sits above every route — so a corrupt value, or
 * a `localStorage` access that throws because the user blocks site data, would
 * throw during render. With no error boundary in the app that blanks the whole
 * page. `process-store.ts` already reads this same key this way.
 *
 * @returns {CachedProcessScope} The cached scope, or an empty scope
 */
function readCachedScope(): CachedProcessScope {
  try {
    const stored = localStorage.getItem('process-scope');
    const parsed: unknown = stored ? JSON.parse(stored) : null;

    // A try/catch only covers a parse *failure*. Valid JSON of the wrong type —
    // `"a string"`, `42`, `["a"]` — parses happily and would be handed on as the
    // scope, leaving every field undefined. Require an object, then overlay it so
    // the result always has the full shape callers destructure.
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      return { ...EMPTY_CACHED_SCOPE };
    }

    return { ...EMPTY_CACHED_SCOPE, ...(parsed as Partial<CachedProcessScope>) };
  } catch (error) {
    console.warn('Failed to read the cached process scope; starting from an empty scope.', error);
    return { ...EMPTY_CACHED_SCOPE };
  }
}

interface ProcessProviderProps {
  children: ReactNode;
  initialScope?: Partial<ProcessScope>;
  initialRightScope?: Partial<ProcessScope>;
  isComparisonView?: boolean;
}

const ProcessProvider = ({
  children,
  initialScope,
  initialRightScope,
  isComparisonView = false,
}: ProcessProviderProps) => {
  // Left (primary) section state
  const [cachedScope, setCachedScope] = useState<CachedProcessScope>(readCachedScope);

  const [eventScope, setEventScope] = useState<EventScope>({
    showEventGraph: false,
    statuses: [],
    statusGroup: null,
    selectedEdgeId: null,
  });

  const [useWorkflow, setUseWorkflow] = useState(false);

  const [startDate, setStartDate] = useState<string | null>(
    initialScope?.startDate || dayjs().subtract(1, 'month').startOf('month').toISOString(),
  );
  const [endDate, setEndDate] = useState<string | null>(initialScope?.endDate || dayjs().toISOString());
  const [taskTypes, setTaskType] = useState<TaskTypeValue[] | null>(initialScope?.taskTypes || null);
  const [stages, setStages] = useState<Stages[]>([]);
  const [workflow, setWorkflow] = useState<ProcessWorkflow | null>(null);
  const [useGroups, setuseGroups] = useState<boolean>(true);

  // Right section state for comparison view
  const [comparisonMode, setComparisonMode] = useState(isComparisonView);
  const [rightCachedScope, setRightCachedScope] = useState<CachedProcessScope>({
    portfolio: initialRightScope?.portfolio || null,
    team: initialRightScope?.team || null,
    board: initialRightScope?.board || null,
    workPeriod: initialRightScope?.workPeriod || null,
    epic: initialRightScope?.epic || null,
    workflow: initialRightScope?.workflow || null,
    task: initialRightScope?.task || null,
  });

  const [rightEventScope, setRightEventScope] = useState<EventScope>({
    showEventGraph: false,
    statuses: [],
    statusGroup: null,
    selectedEdgeId: null,
  });

  const [rightUseWorkflow, setRightUseWorkflow] = useState(initialRightScope?.useWorkflow || false);

  const [rightStartDate, setRightStartDate] = useState<string | null>(
    initialRightScope?.startDate || dayjs().subtract(1, 'month').startOf('month').toISOString(),
  );
  const [rightEndDate, setRightEndDate] = useState<string | null>(initialRightScope?.endDate || dayjs().toISOString());
  const [rightTaskType, setRightTaskType] = useState<TaskTypeValue[] | null>(initialRightScope?.taskTypes || null);
  const [rightStages, setRightStages] = useState<Stages[]>([]);
  const [rightWorkflow, setRightWorkflow] = useState<ProcessWorkflow | null>(null);
  const [rightUseGroups, setRightUseGroups] = useState<boolean>(initialRightScope?.useGroups || true);

  // Use a ref to track initialScope changes
  const initialScopeRef = useRef(initialScope);
  const initialRightScopeRef = useRef(initialRightScope);

  // Initialize with initialScope if provided - only run once
  useEffect(() => {
    if (initialScope) {
      const newCachedScope: CachedProcessScope = { ...cachedScope };

      if (initialScope.portfolio !== undefined) {
        newCachedScope.portfolio = initialScope.portfolio;
      }

      if (initialScope.team !== undefined) {
        newCachedScope.team = initialScope.team;
      }

      if (initialScope.board !== undefined) {
        newCachedScope.board = initialScope.board;
      }

      if (initialScope.epic !== undefined) {
        newCachedScope.epic = initialScope.epic;
      }

      if (!isEqual(newCachedScope, cachedScope)) {
        setCachedScope(newCachedScope);
      }

      if (initialScope.startDate !== undefined) {
        setStartDate(initialScope.startDate);
      }

      if (initialScope.endDate !== undefined) {
        setEndDate(initialScope.endDate);
      }

      if (initialScope.taskTypes !== undefined) {
        setTaskType(initialScope.taskTypes);
      }

      if (initialScope.useWorkflow !== undefined) {
        setUseWorkflow(initialScope.useWorkflow);
      }

      if (initialScope.useGroups !== undefined) {
        setuseGroups(!!initialScope.useGroups);
      }

      // Store initial values
      initialScopeRef.current = initialScope;
    }
  }, [initialScope, cachedScope]); // Only run once on mount

  // Initialize right section with initialRightScope if provided
  useEffect(() => {
    if (initialRightScope) {
      const newRightCachedScope: CachedProcessScope = { ...rightCachedScope };

      if (initialRightScope.portfolio !== undefined) {
        newRightCachedScope.portfolio = initialRightScope.portfolio;
      }

      if (initialRightScope.team !== undefined) {
        newRightCachedScope.team = initialRightScope.team;
      }

      if (initialRightScope.board !== undefined) {
        newRightCachedScope.board = initialRightScope.board;
      }

      if (initialRightScope.epic !== undefined) {
        newRightCachedScope.epic = initialRightScope.epic;
      }

      if (!isEqual(newRightCachedScope, rightCachedScope)) {
        setRightCachedScope(newRightCachedScope);
      }

      if (initialRightScope.startDate !== undefined) {
        setRightStartDate(initialRightScope.startDate);
      }

      if (initialRightScope.endDate !== undefined) {
        setRightEndDate(initialRightScope.endDate);
      }

      if (initialRightScope.taskTypes !== undefined) {
        setRightTaskType(initialRightScope.taskTypes);
      }

      if (initialRightScope.useWorkflow !== undefined) {
        setRightUseWorkflow(initialRightScope.useWorkflow);
      }

      if (initialRightScope.useGroups !== undefined) {
        setRightUseGroups(!!initialRightScope.useGroups);
      }

      // Store initial values
      initialRightScopeRef.current = initialRightScope;
    }
  }, [initialRightScope, rightCachedScope]); // Only run once on mount

  // Update when initialScope changes
  useEffect(() => {
    if (initialScope && initialScopeRef.current !== initialScope) {
      // Check if any values have actually changed
      let hasChanged = false;

      // Check for team/board changes
      if (JSON.stringify(initialScope.team) !== JSON.stringify(initialScopeRef.current?.team)) {
        setCachedScope((prev) => ({ ...prev, team: initialScope.team || null }));
        hasChanged = true;
      }

      if (JSON.stringify(initialScope.board) !== JSON.stringify(initialScopeRef.current?.board)) {
        setCachedScope((prev) => ({ ...prev, board: initialScope.board || null }));
        hasChanged = true;
      }

      if (JSON.stringify(initialScope.epic) !== JSON.stringify(initialScopeRef.current?.epic)) {
        setCachedScope((prev) => ({ ...prev, epic: initialScope.epic || null }));
        hasChanged = true;
      }

      // Check for date/task type changes
      if (initialScope.startDate !== initialScopeRef.current?.startDate) {
        setStartDate(initialScope.startDate || null);
        hasChanged = true;
      }

      if (initialScope.endDate !== initialScopeRef.current?.endDate) {
        setEndDate(initialScope.endDate || null);
        hasChanged = true;
      }

      if (initialScope.taskTypes !== initialScopeRef.current?.taskTypes) {
        setTaskType(initialScope.taskTypes || null);
        hasChanged = true;
      }

      if (initialScope.useWorkflow !== initialScopeRef.current?.useWorkflow) {
        setUseWorkflow(initialScope.useWorkflow || false);
        hasChanged = true;
      }

      if (initialScope.useGroups !== initialScopeRef.current?.useGroups) {
        setuseGroups(!!initialScope.useGroups);
        hasChanged = true;
      }

      if (hasChanged) {
        initialScopeRef.current = initialScope;
      }
    }
  }, [initialScope]);

  // Update when initialRightScope changes
  useEffect(() => {
    if (initialRightScope && initialRightScopeRef.current !== initialRightScope) {
      // Check if any values have actually changed
      let hasChanged = false;

      // Check for team/board changes
      if (JSON.stringify(initialRightScope.team) !== JSON.stringify(initialRightScopeRef.current?.team)) {
        setRightCachedScope((prev) => ({ ...prev, team: initialRightScope.team || null }));
        hasChanged = true;
      }

      if (JSON.stringify(initialRightScope.board) !== JSON.stringify(initialRightScopeRef.current?.board)) {
        setRightCachedScope((prev) => ({ ...prev, board: initialRightScope.board || null }));
        hasChanged = true;
      }

      if (JSON.stringify(initialRightScope.epic) !== JSON.stringify(initialRightScopeRef.current?.epic)) {
        setRightCachedScope((prev) => ({ ...prev, epic: initialRightScope.epic || null }));
        hasChanged = true;
      }

      // Check for date/task type changes
      if (initialRightScope.startDate !== initialRightScopeRef.current?.startDate) {
        setRightStartDate(initialRightScope.startDate || null);
        hasChanged = true;
      }

      if (initialRightScope.endDate !== initialRightScopeRef.current?.endDate) {
        setRightEndDate(initialRightScope.endDate || null);
        hasChanged = true;
      }

      if (initialRightScope.taskTypes !== initialRightScopeRef.current?.taskTypes) {
        setRightTaskType(initialRightScope.taskTypes || null);
        hasChanged = true;
      }

      if (initialRightScope.useWorkflow !== initialRightScopeRef.current?.useWorkflow) {
        setRightUseWorkflow(initialRightScope.useWorkflow || false);
        hasChanged = true;
      }

      if (initialRightScope.useGroups !== initialRightScopeRef.current?.useGroups) {
        setRightUseGroups(!!initialRightScope.useGroups);
        hasChanged = true;
      }

      if (hasChanged) {
        initialRightScopeRef.current = initialRightScope;
      }
    }
  }, [initialRightScope]);

  const setScope = useCallback(
    (newScope: Partial<ProcessScope>) => {
      const defaultStartDate = dayjs().subtract(1, 'month').startOf('month').toISOString();
      const defaultEndDate = dayjs().toISOString();

      if (!('startDate' in newScope)) {
        setStartDate(defaultStartDate);
      }
      if (!('endDate' in newScope)) {
        setEndDate(defaultEndDate);
      }
      if (!('taskTypes' in newScope)) {
        setTaskType(null);
      }
      if (!('useWorkflow' in newScope)) {
        setUseWorkflow(false);
      }
      if (!('useGroups' in newScope)) {
        setuseGroups(true);
      }

      let newCachedScope = { ...cachedScope };
      let hasChanged = false;

      // Handle portfolio changes
      if ('portfolio' in newScope) {
        newCachedScope = {
          ...newCachedScope,
          portfolio: newScope.portfolio === undefined ? null : newScope.portfolio,
          team: null,
          board: null,
          epic: null,
        };
        hasChanged = true;
        // Clear taskTypes when portfolio changes
        setTaskType(null);
      }

      // Handle team changes (including setting to null)
      if ('team' in newScope) {
        const newTeam = newScope.team === undefined ? null : newScope.team;
        const oldTeam = cachedScope.team;
        const isTeamChanged = !newTeam || !oldTeam || oldTeam.id !== newTeam.id;

        newCachedScope = {
          ...newCachedScope,
          team: newTeam,
          // Clear dependent fields when team changes or is null
          board: isTeamChanged ? null : newCachedScope.board,
          epic: isTeamChanged ? null : newCachedScope.epic,
        };
        hasChanged = true;
        setTaskType(null);
      }

      // Handle board changes (including setting to null)
      if ('board' in newScope) {
        const newBoard = newScope.board === undefined ? null : newScope.board;
        newCachedScope = {
          ...newCachedScope,
          board: newBoard,
          // Clear epic when board is null
          epic: newBoard ? newCachedScope.epic : null,
        };
        hasChanged = true;
        // Clear taskTypes when board changes
        setTaskType(null);
      }

      // Handle epic changes (including setting to null)
      if ('epic' in newScope) {
        const newEpic = newScope.epic === undefined ? null : newScope.epic;
        newCachedScope = {
          ...newCachedScope,
          epic: newEpic,
        };
        hasChanged = true;
      }

      // Handle workflow changes
      if ('workflow' in newScope) {
        const newWorkflow = newScope.workflow === undefined ? null : newScope.workflow;
        newCachedScope = {
          ...newCachedScope,
          workflow: newWorkflow,
        };
        hasChanged = true;
      }

      // Update cached scope if changed
      if (hasChanged) {
        setCachedScope(newCachedScope);
        // Guarded for the same reason as the store-side write: a quota error
        // here would propagate out of the scope update and blank the page.
        try {
          localStorage.setItem('process-scope', JSON.stringify(newCachedScope));
        } catch (error) {
          console.warn('Failed to cache the process scope; continuing without it.', error);
        }
      }

      // Handle taskTypes changes (including null)
      if ('taskTypes' in newScope) {
        const newTaskType = newScope.taskTypes === undefined ? null : newScope.taskTypes;
        setTaskType(newTaskType);
      }

      // Handle date changes
      if (newScope.startDate !== undefined) {
        setStartDate(newScope.startDate);
      }

      if (newScope.endDate !== undefined) {
        setEndDate(newScope.endDate);
      }

      if (newScope.useWorkflow !== undefined) {
        setUseWorkflow(newScope.useWorkflow || false);
      }

      if (newScope.useGroups !== undefined) {
        setuseGroups(!!newScope.useGroups);
      }
    },
    [cachedScope],
  );

  const setRightScope = useCallback(
    (newScope: Partial<ProcessScope>) => {
      const defaultStartDate = dayjs().subtract(1, 'month').startOf('month').toISOString();
      const defaultEndDate = dayjs().toISOString();

      if (!('startDate' in newScope)) {
        setRightStartDate(defaultStartDate);
      }
      if (!('endDate' in newScope)) {
        setRightEndDate(defaultEndDate);
      }
      if (!('taskTypes' in newScope)) {
        setRightTaskType(null);
      }
      if (!('useWorkflow' in newScope)) {
        setRightUseWorkflow(false);
      }
      if (!('useGroups' in newScope)) {
        setRightUseGroups(true);
      }

      let newRightCachedScope = { ...rightCachedScope };
      let hasChanged = false;

      // Handle portfolio changes
      if ('portfolio' in newScope) {
        newRightCachedScope = {
          ...newRightCachedScope,
          portfolio: newScope.portfolio === undefined ? null : newScope.portfolio,
          team: null,
          board: null,
          epic: null,
        };
        hasChanged = true;
        // Clear taskTypes when portfolio changes
        setRightTaskType(null);
      }

      // Handle team changes (including setting to null)
      if ('team' in newScope) {
        const newTeam = newScope.team === undefined ? null : newScope.team;
        const oldTeam = rightCachedScope.team;
        const isTeamChanged = !newTeam || !oldTeam || oldTeam.id !== newTeam.id;

        newRightCachedScope = {
          ...newRightCachedScope,
          team: newTeam,
          // Clear dependent fields when team changes or is null
          board: isTeamChanged ? null : newRightCachedScope.board,
          epic: isTeamChanged ? null : newRightCachedScope.epic,
        };
        hasChanged = true;
        // Clear taskTypes when team changes
        setRightTaskType(null);
      }

      // Handle board changes (including setting to null)
      if ('board' in newScope) {
        const newBoard = newScope.board === undefined ? null : newScope.board;
        newRightCachedScope = {
          ...newRightCachedScope,
          board: newBoard,
          // Clear epic when board is null
          epic: newBoard ? newRightCachedScope.epic : null,
        };
        hasChanged = true;
        // Clear taskTypes when board changes
        setRightTaskType(null);
      }

      // Handle epic changes (including setting to null)
      if ('epic' in newScope) {
        const newEpic = newScope.epic === undefined ? null : newScope.epic;
        newRightCachedScope = {
          ...newRightCachedScope,
          epic: newEpic,
        };
        hasChanged = true;
      }

      // Handle workflow changes
      if ('workflow' in newScope) {
        const newWorkflow = newScope.workflow === undefined ? null : newScope.workflow;
        newRightCachedScope = {
          ...newRightCachedScope,
          workflow: newWorkflow,
        };
        hasChanged = true;
      }

      // Update cached scope if changed
      if (hasChanged) {
        setRightCachedScope(newRightCachedScope);
      }

      // Handle taskTypes changes (including null)
      if ('taskTypes' in newScope) {
        const newTaskType = newScope.taskTypes === undefined ? null : newScope.taskTypes;
        setRightTaskType(newTaskType);
      }

      // Handle date changes
      if (newScope.startDate !== undefined) {
        setRightStartDate(newScope.startDate);
      }

      if (newScope.endDate !== undefined) {
        setRightEndDate(newScope.endDate);
      }

      if (newScope.useWorkflow !== undefined) {
        setRightUseWorkflow(newScope.useWorkflow || false);
      }

      if (newScope.useGroups !== undefined) {
        setRightUseGroups(!!newScope.useGroups);
      }
    },
    [rightCachedScope],
  );

  return (
    <ProcessContext.Provider
      value={{
        scope: {
          ...cachedScope,
          startDate,
          endDate,
          taskTypes,
          useWorkflow,
          useGroups,
        },
        setScope,
        stages,
        setStages,
        workflow,
        setWorkflow,
        eventScope,
        setEventScope,
        // Comparison view properties
        isComparisonView: comparisonMode,
        setIsComparisonView: setComparisonMode,
        rightScope: {
          ...rightCachedScope,
          startDate: rightStartDate,
          endDate: rightEndDate,
          taskTypes: rightTaskType,
          useWorkflow: rightUseWorkflow,
          useGroups: rightUseGroups,
        },
        setRightScope,
        rightStages,
        setRightStages,
        rightWorkflow,
        setRightWorkflow,
        rightEventScope,
        setRightEventScope,
      }}
    >
      {children}
    </ProcessContext.Provider>
  );
};

export { ProcessContext, ProcessProvider, readCachedScope };
